<template>
  <div class="ai-writer-dispatcher">
    <div v-if="mode === 'loading'" class="ai-writer-dispatcher__loading">
      <span class="loader"></span>
      <span>Loading AI Writer…</span>
    </div>

    <ai-writer-v2
      v-else-if="mode === 'v2'"
      ref="child"
      :aiRunId="aiRunId"
      :pageRunId="pageRunId"
      :pageRun="pageRun"
      :page="page"
      :runStrategies="runStrategies"
      :currentWordCount="currentWordCount"
      :targetWordCount="targetWordCount"
      :initialSession="v2Session"
      v-on="$listeners"
    />

    <legacy-ai-writer
      v-else
      ref="child"
      :aiRunId="aiRunId"
      :pageRunId="pageRunId"
      :pageRun="pageRun"
      :page="page"
      :runStrategies="runStrategies"
      :currentWordCount="currentWordCount"
      :targetWordCount="targetWordCount"
      :readOnly="legacyReadOnly"
      v-on="$listeners"
    />
  </div>
</template>

<script>
// Dispatcher: routes between the legacy AI Writer (schema_version 1) and the
// redesigned v2 flow (schema_version 2), without moving the large legacy
// component. New runs go to v2 only when the AI_WRITER_V2_ENABLED flag is on.
import LegacyAiWriter from "@/components/FullPageSourceEditorV2/components/AiWriter/Index.vue";
import AiWriterV2 from "@/components/FullPageSourceEditorV2/components/AiWriter/v2/Index.vue";
import { aiWriterGetState, aiWriterGetLatest } from "@/api";

export default {
  name: "AiWriterDispatcher",
  components: { LegacyAiWriter, AiWriterV2 },
  props: [
    "aiRunId",
    "pageRunId",
    "pageRun",
    "runStrategies",
    "page",
    "currentWordCount",
    "targetWordCount",
  ],
  data() {
    return {
      mode: "loading", // 'loading' | 'legacy' | 'v2'
      v2Session: null,
    };
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    v2Enabled() {
      return !!this.$store.state.popCreditCharges?.aiWriterV2Enabled;
    },
    // After cutover (v2 flag on), existing v1 runs render read-only — viewable
    // history, but the old wizard can't trigger new generation/charges. Before
    // cutover the legacy flow stays fully editable (it's still the live product).
    legacyReadOnly() {
      return this.v2Enabled && !!this.aiRunId;
    },
  },
  async mounted() {
    await this.decide();
  },
  methods: {
    async decide() {
      // Existing run → detect schema version from the v2 state endpoint.
      if (this.aiRunId) {
        try {
          const res = await aiWriterGetState(this.token, this.aiRunId);
          const session = res?.data?.session;
          if (res?.data?.status === "SUCCESS" && session?.schemaVersion === 2) {
            this.v2Session = session;
            this.mode = "v2";
            return;
          }
        } catch (e) {
          // fall through to legacy
        }
        this.mode = "legacy";
        return;
      }

      // No explicit run requested. When v2 is on, resume the latest v2 session
      // for this page run (ongoing or completed) so reopening drops the user
      // back where they left off instead of always starting fresh.
      if (this.v2Enabled && this.pageRunId) {
        try {
          const res = await aiWriterGetLatest(this.token, this.pageRunId);
          const session = res?.data?.session;
          if (res?.data?.status === "SUCCESS" && session?.schemaVersion === 2) {
            this.v2Session = session;
            this.mode = "v2";
            return;
          }
        } catch (e) {
          // fall through to a fresh session
        }
      }

      // Brand-new run → v2 only when the flag is on.
      this.mode = this.v2Enabled ? "v2" : "legacy";
    },
    // Delegated from the parent, which keeps a ref to <ai-writer> and calls
    // startOverAgain() on it. Forward to the active child if it supports it.
    startOverAgain() {
      const child = this.$refs.child;
      if (child && typeof child.startOverAgain === "function") {
        child.startOverAgain();
      }
    },
  },
};
</script>

<style scoped>
.ai-writer-dispatcher__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  font-family: "Mulish", system-ui, sans-serif;
  color: #6b7b7e;
}
.ai-writer-dispatcher__loading .loader {
  width: 18px;
  height: 18px;
  border: 2px solid #e4e4e4;
  border-top-color: #ff5700;
  border-radius: 50%;
  animation: aiwd-spin 0.8s linear infinite;
}
@keyframes aiwd-spin {
  to { transform: rotate(360deg); }
}
</style>
